import { inject, injectable } from "inversify";
import axios, { CancelToken } from "axios";
import { API_ENDPOINTS } from "@infrastructure/InfrastructureConstants";
import { runtimeConfig } from "@utils/runtimeConfig/runtimeConfig";
import { TYPES } from "@utils/inversifyJS/types";
import type { IAuthenticatedRequestHandlerService } from "@application/contracts/services";
import type { IRawFileRepository } from "@domain/repositories/IRawFileRepository";
import { PagedBatchRawFileList } from "@domain/schemas/BI";

@injectable()
export class RawFileRepository implements IRawFileRepository {
  private static readonly CHUNK_SIZE_BYTES = 8 * 1024 * 1024;

  @inject(TYPES.IAuthenticatedRequestHandlerService)
  private _requestHandlerService!: IAuthenticatedRequestHandlerService;

  async getRawFiles(folder: string): Promise<PagedBatchRawFileList> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.rawFiles}?folder=${folder}`;
    return this._requestHandlerService.handleRequestAsync<PagedBatchRawFileList>(() => axios.get<PagedBatchRawFileList>(url));
  }

  async uploadRawFile(
    file: File,
    folder: string,
    onProgress?: (progress: number) => void,
    cancelToken?: CancelToken
  ): Promise<void> {
    const initUrl = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.rawFilesUploadInit}`;
    const chunkUrl = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.rawFilesUploadChunk}`;
    const completeUrl = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.rawFilesUploadComplete}`;
    const totalChunks = Math.max(1, Math.ceil(file.size / RawFileRepository.CHUNK_SIZE_BYTES));

    const uploadId = await this._requestHandlerService.handleRequestAsync<string>(() =>
      axios.post<string>(initUrl, {
        fileName: file.name,
        folder,
        fileLength: file.size,
      }, {
        timeout: 24 * 60 * 60 * 1000,
        cancelToken,
      })
    );

    const partTokens: string[] = [];
    let completedBytes = 0;

    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
      const start = chunkIndex * RawFileRepository.CHUNK_SIZE_BYTES;
      const end = Math.min(start + RawFileRepository.CHUNK_SIZE_BYTES, file.size);
      const chunk = file.slice(start, end);
      const partNumber = chunkIndex + 1;

      const partToken = await this._requestHandlerService.handleRequestAsync<string>(() =>
        axios.post<string>(chunkUrl, chunk, {
          params: {
            uploadId,
            partNumber,
            fileName: file.name,
            folder,
          },
          headers: {
            "Content-Type": "application/octet-stream",
          },
          timeout: 24 * 60 * 60 * 1000,
          cancelToken,
          onUploadProgress: (progressEvent) => {
            if (!onProgress) {
              return;
            }

            const loaded = Math.min(completedBytes + (progressEvent.loaded ?? 0), file.size);
            const percentCompleted = Math.min(99, Math.round((loaded * 100) / (file.size || 1)));
            onProgress(percentCompleted);
          },
        })
      );

      partTokens.push(partToken);
      completedBytes = end;

      if (onProgress) {
        const percentCompleted = chunkIndex === totalChunks - 1
          ? 99
          : Math.min(99, Math.round((completedBytes * 100) / (file.size || 1)));
        onProgress(percentCompleted);
      }
    }

    await this._requestHandlerService.handleRequestAsync<void>(() =>
      axios.post<void>(completeUrl, {
        uploadId,
        partTokens,
        fileName: file.name,
        folder,
        md5Hash: null,
      }, {
        timeout: 24 * 60 * 60 * 1000,
        cancelToken,
      })
    );
  }

  async checkFileExists(fileName: string, folder?: string): Promise<number | null> {
    let url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.checkFile}?fileName=${fileName}`;
    if (folder) {
      url += `&folder=${folder}`;
    }
    const response = await this._requestHandlerService.handleRequestAsync<number | null>(() => axios.get<number | null>(url));
    return response;
  }

  async checkFilesExist(fileNames: string[], folder?: string): Promise<Record<string, number | null>> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.checkFiles}`;
    const payload = { fileNames, folder };
    const response = await this._requestHandlerService.handleRequestAsync<Record<string, number | null>>(() => axios.post<Record<string, number | null>>(url, payload));
    return response;
  }
}
